<template>
  <div class="site-maintenance-cycle-tab">
    <!-- 顶部操作栏与概览 -->
    <div class="table-operator" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px;">
      <div>
        <a-button type="primary" icon="plus" @click="handleAdd" :disabled="!siteId">新建运维周期规则</a-button>
        <a-button icon="reload" @click="loadData" style="margin-left: 8px;">刷 新</a-button>
      </div>
      <div v-if="dataSource.length > 0" class="rule-count-tip">
        当前站点共配置了 <span class="rule-count-num">{{ dataSource.length }}</span> 条运维周期规则
      </div>
    </div>

    <!-- 规则表格（自适应 100% 宽度，无横向滚动条） -->
    <a-table
      ref="table"
      size="middle"
      bordered
      rowKey="id"
      :columns="columns"
      :dataSource="dataSource"
      :pagination="false"
      :loading="loading"
      class="cycle-table-fit"
    >
      <!-- 运维项目 + 类型 + 运维对象 (聚合层次展示) -->
      <template slot="name" slot-scope="text, record">
        <div class="project-col">
          <span class="project-title">{{ text }}</span>
          <div class="project-tags">
            <span class="type-badge">{{ record.businessName || record.businessType }}</span>
            <span class="scope-badge">{{ record.scopeLabel || (record.scopeKey === '*' ? '整站通用' : record.scopeKey) }}</span>
          </div>
        </div>
      </template>

      <!-- 周期展示 -->
      <template slot="period" slot-scope="text, record">
        <a-tag :color="getPeriodTagColor(record.periodUnit)" class="period-tag">
          每 {{ record.periodValue }} {{ getUnitText(record.periodUnit) }}
        </a-tag>
      </template>

      <!-- 负责角色展示 -->
      <template slot="roles" slot-scope="text, record">
        <template v-if="getRoleLabels(record).length > 0">
          <div class="roles-wrap">
            <a-tag v-for="role in getRoleLabels(record)" :key="role" color="blue" class="role-tag">
              {{ role }}
            </a-tag>
          </div>
        </template>
        <span v-else class="role-empty-text">未分配角色</span>
      </template>

      <!-- 下次执行时间 + 相对倒计时 + 上次基准 (聚合展示) -->
      <template slot="schedule" slot-scope="text, record">
        <div class="schedule-col">
          <div v-if="record.dueAt" class="due-line">
            <span :class="{ 'overdue-text': isOverdue(record.dueAt) }">
              <a-icon type="clock-circle" style="margin-right: 3px;" />
              {{ record.dueAt }}
            </span>
            <span class="due-relative" :class="{ 'overdue-badge': isOverdue(record.dueAt) }">
              {{ getRelativeDueDesc(record.dueAt) }}
            </span>
          </div>
          <div v-else class="due-empty">-</div>

          <div class="last-completed-sub">
            上次基准: {{ record.lastCompletedAt || '冷启动无历史' }}
          </div>
        </div>
      </template>

      <!-- 启用状态切换 -->
      <template slot="enabled" slot-scope="text, record">
        <a-switch
          size="small"
          :checked="record.enabled === 1 || record.enabled === true"
          checked-children="启"
          un-checked-children="停"
          @change="(checked) => handleToggleEnabled(record, checked)"
        />
      </template>

      <!-- 操作列 -->
      <template slot="action" slot-scope="text, record">
        <a @click="handleEdit(record)">编辑</a>
        <a-divider type="vertical" />
        <a-popconfirm title="确定要归档该周期规则吗？归档后将取消当前未完成任务并保留历史记录。" @confirm="handleArchive(record)">
          <a style="color: #ff4d4f;">归档</a>
        </a-popconfirm>
      </template>
    </a-table>

    <!-- 规则弹窗 -->
    <maintenance-cycle-modal ref="modalForm" @ok="loadData" />
  </div>
</template>

<script>
import { getAction, putAction, deleteAction } from '@/api/manage'
import { initDictOptions } from '@/components/dict/JDictSelectUtil'
import moment from 'moment'
import MaintenanceCycleModal from './modules/MaintenanceCycleModal.vue'

export default {
  name: 'SiteMaintenanceCycleTab',
  components: {
    MaintenanceCycleModal
  },
  props: {
    siteId: {
      type: [Number, String],
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      dataSource: [],
      roleDictMap: {},
      columns: [
        {
          title: '运维项目 / 类型 / 对象',
          dataIndex: 'name',
          align: 'left',
          scopedSlots: { customRender: 'name' }
        },
        {
          title: '执行周期',
          align: 'center',
          width: 95,
          scopedSlots: { customRender: 'period' }
        },
        {
          title: '负责角色',
          align: 'center',
          width: 140,
          scopedSlots: { customRender: 'roles' }
        },
        {
          title: '下次执行 / 历史基准',
          align: 'center',
          width: 190,
          scopedSlots: { customRender: 'schedule' }
        },
        {
          title: '状态',
          dataIndex: 'enabled',
          align: 'center',
          width: 65,
          scopedSlots: { customRender: 'enabled' }
        },
        {
          title: '操作',
          align: 'center',
          width: 95,
          scopedSlots: { customRender: 'action' }
        }
      ]
    }
  },
  watch: {
    siteId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadData()
        } else {
          this.dataSource = []
        }
      }
    }
  },
  created() {
    this.loadRoleDict()
  },
  methods: {
    loadRoleDict() {
      initDictOptions('site_role').then(res => {
        const items = (res && res.result) ? res.result : (Array.isArray(res) ? res : [])
        const map = {}
        items.forEach(it => {
          map[String(it.value)] = it.text
        })
        this.roleDictMap = map
      }).catch(() => {})
    },
    loadData() {
      if (!this.siteId) return
      this.loading = true
      getAction(`/om/maintenance/cycles?siteId=${this.siteId}&archived=false`).then(res => {
        if (res.success && res.result) {
          this.dataSource = res.result
        } else {
          this.dataSource = []
        }
      }).finally(() => {
        this.loading = false
      })
    },
    handleAdd() {
      if (!this.siteId) {
        this.$message.warning('请先选择或保存站点')
        return
      }
      this.$refs.modalForm.add(this.siteId)
    },
    handleEdit(record) {
      this.$refs.modalForm.edit(record)
    },
    handleToggleEnabled(record, checked) {
      const targetEnabled = checked ? 1 : 0
      const putData = Object.assign({}, record, {
        enabled: targetEnabled,
        siteId: this.siteId
      })
      putAction(`/om/maintenance/cycles/${record.id}`, putData).then(res => {
        if (res.success) {
          this.$message.success(checked ? '已启用该规则' : '已停用该规则')
          this.loadData()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      })
    },
    handleArchive(record) {
      deleteAction(`/om/maintenance/cycles/${record.id}?version=${record.version}`).then(res => {
        if (res.success) {
          this.$message.success('规则归档成功')
          this.loadData()
        } else {
          this.$message.error(res.message || '归档失败')
        }
      })
    },
    getUnitText(unit) {
      switch (unit) {
        case 'DAY': return '天'
        case 'WEEK': return '周'
        case 'MONTH': return '个月'
        default: return unit || '天'
      }
    },
    getPeriodTagColor(unit) {
      switch (unit) {
        case 'DAY': return 'orange'
        case 'WEEK': return 'blue'
        case 'MONTH': return 'green'
        default: return 'cyan'
      }
    },
    getRoleLabels(record) {
      let roleCodes = []
      if (Array.isArray(record.roleCodeList)) {
        roleCodes = record.roleCodeList
      } else if (typeof record.roleCodes === 'string') {
        try {
          const parsed = JSON.parse(record.roleCodes)
          roleCodes = Array.isArray(parsed) ? parsed : [record.roleCodes]
        } catch (e) {
          roleCodes = record.roleCodes.split(',').filter(Boolean)
        }
      }
      return roleCodes.map(code => this.roleDictMap[String(code)] || `角色${code}`)
    },
    isOverdue(dueAt) {
      if (!dueAt) return false
      return new Date(dueAt).getTime() < new Date().getTime()
    },
    getRelativeDueDesc(dueAt) {
      if (!dueAt) return ''
      const target = moment(dueAt)
      const now = moment()
      const diffDays = target.diff(now, 'days')
      const diffHours = target.diff(now, 'hours')

      if (diffHours < 0) {
        const overdueDays = Math.abs(diffDays)
        return overdueDays === 0 ? '(已逾期)' : `(逾期${overdueDays}天)`
      }
      if (diffDays === 0) {
        return '(今天)'
      } else if (diffDays === 1) {
        return '(明天)'
      } else {
        return `(${diffDays}天后)`
      }
    }
  }
}
</script>

<style lang="less" scoped>
.site-maintenance-cycle-tab {
  padding: 4px 0;
}

.rule-count-tip {
  color: #a6c8ff;
  font-size: 13px;
}
.rule-count-num {
  color: #40a9ff;
  font-weight: 600;
}

/* 运维项目列聚合样式 */
.project-col {
  .project-title {
    font-weight: 600;
    font-size: 14px;
    line-height: 1.4;
    display: block;
    margin-bottom: 4px;
  }
  .project-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;

    .type-badge {
      font-size: 11px;
      color: #1890ff;
      background: rgba(24, 144, 255, 0.12);
      border: 1px solid rgba(24, 144, 255, 0.3);
      padding: 0 5px;
      border-radius: 3px;
    }
    .scope-badge {
      font-size: 11px;
      color: #52c41a;
      background: rgba(82, 196, 26, 0.12);
      border: 1px solid rgba(82, 196, 26, 0.3);
      padding: 0 5px;
      border-radius: 3px;
    }
  }
}

.period-tag {
  margin: 0;
  font-weight: 500;
}

.roles-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  justify-content: center;
  .role-tag {
    margin: 0;
    font-size: 12px;
  }
}

.role-empty-text {
  color: #faad14;
  font-size: 12px;
}

.schedule-col {
  text-align: center;
  .due-line {
    font-size: 13px;
    line-height: 1.3;
  }
  .due-empty {
    color: #999;
  }
  .due-relative {
    font-size: 12px;
    color: #40a9ff;
    margin-left: 3px;
  }
  .overdue-text {
    color: #ff4d4f;
    font-weight: 600;
  }
  .overdue-badge {
    color: #ff4d4f;
    font-weight: 600;
  }
  .last-completed-sub {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.45);
    margin-top: 2px;
  }
}

/* 亮色模式适配 */
.lightColor {
  .rule-count-tip {
    color: #8c8c8c;
  }
  .rule-count-num {
    color: #1890ff;
  }
  .last-completed-sub {
    color: rgba(0, 0, 0, 0.45);
  }
}
</style>
